import React from 'react';
import type { ImageProps } from '@archipro-design/aria';
import { Button, pxToRem, useStyles, VideoPlayer } from '@archipro-design/aria';
import * as S from './VideoBannerWithOptimistic.style';
import { Trash } from '@archipro-design/icons';
import { HoverAction } from '@modules/page-builder/component/common/hover-action/HoverAction';
import type { ColumnDimensionType } from '@modules/page-builder/types';
import PreviewImage from '@modules/page-builder/component/edit/page-builder-image/PreviewImage';
import { ClientOnly } from 'remix-utils';
import { useTracker } from '@archipro-website/tracker';

interface VideoBannerWithOptimisticProps {
    dimension: ColumnDimensionType;
    videoBannerUrl: string;
    imageUrl?: string;
    imageOptimistic?: string;
    onDelete?: () => void;
    playsInline: boolean;
    autoplay?: boolean;
    loop?: boolean;
    hideControls?: boolean;
    isDeleting?: boolean;
}

const VideoBannerWithOptimistic = ({
    dimension,
    videoBannerUrl,
    imageUrl,
    imageOptimistic,
    onDelete,
    playsInline,
    autoplay = false,
    loop = false,
    hideControls = false,
    isDeleting = false,
}: VideoBannerWithOptimisticProps) => {
    const { css } = useStyles({ dimension });
    const [loaded, setLoaded] = React.useState(!!imageUrl);
    const [optimisticImage, setOptimisticImage] =
        React.useState(imageOptimistic);

    const tracker = useTracker();

    const imageProps: Omit<ImageProps, 'src'> = {
        layout: 'fill',
        width: dimension.width,
        height: dimension.height,
        objectFit: 'cover',
        fadeIn: false,
        alt: '',
    };

    const videoPlayer = (
        <VideoPlayer
            src={videoBannerUrl}
            width={dimension.width}
            height={dimension.height}
            coverImage={{
                src: imageUrl ?? optimisticImage ?? '',
                width: dimension.width,
                height: dimension.height,
                layout: 'intrinsic',
                objectFit: 'cover',
                objectPosition: 'center',
                alt: '',
            }}
            muted={true}
            controls={!hideControls}
            playsInline={playsInline}
            onCoverLoadComplete={() => {
                setLoaded(true);
                setOptimisticImage(undefined);
            }}
            variables={{
                playIconSizeDesktop: 0,
                playIconSizeMobile: 0,
                coverImagePosition: 'absolute',
            }}
            playing={!!autoplay}
            loop={loop}
            trackPlayEvent={() => {
                tracker.log('Video', {
                    url: new URL(window.location.href),
                });
            }}
        />
    );

    return (
        <div className={css(S.AspectRatioBox)}>
            <>
                {playsInline ? (
                    <ClientOnly fallback={<></>}>
                        {() => videoPlayer}
                    </ClientOnly>
                ) : (
                    videoPlayer
                )}
                <div
                    className={`${css(S.HoverActions)} hover-buttons-image`}
                    style={{
                        pointerEvents: isDeleting ? 'none' : 'auto',
                    }}
                >
                    <HoverAction
                        label={isDeleting ? 'Removing...' : 'Remove'}
                        action={
                            <Button
                                onClick={isDeleting ? undefined : onDelete}
                                icon={<Trash />}
                                size={24}
                                color={'white'}
                                disabled={isDeleting}
                                variables={{
                                    buttonPadding: pxToRem(8),
                                }}
                            />
                        }
                    />
                </div>
                <PreviewImage
                    show={loaded}
                    imageUrl={optimisticImage}
                    imageProps={imageProps}
                />
            </>
        </div>
    );
};

export default VideoBannerWithOptimistic;
